import { useState } from "react";
import {
  attendeeOptions,
  countryCodes,
  features,
  industries,
} from "./resource";

const RequestDemo = (props) => {
  const [formData, setFormData] = useState(
    props.data
      ? props.data
      : {
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          countryCode: "US +1",
          companyName: "",
          eventName: "",
          attendees: "",
          industry: "",
          features: [],
          message: "",
          agreeToPolicy: true,
        }
  );

  const [errors, setErrors] = useState({});

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic required field validation
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.companyName.trim())
      newErrors.companyName = "Company name is required";
    if (!formData.eventName.trim())
      newErrors.eventName = "Event name is required";
    if (!formData.attendees.trim())
      newErrors.attendees = "Select number of attendees";
    if (!formData.industry.trim()) newErrors.industry = "Select industry";
    if (!formData.message.trim())
      newErrors.message = "Event description is required";
    if (!formData.agreeToPolicy)
      newErrors.agreeToPolicy = "You must agree to the policy";

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      // Remove all non-digit characters for validation
      const phoneDigits = formData.phone.replace(/\D/g, "");
      if (phoneDigits.length < 10) {
        newErrors.phone =
          "Please enter a valid phone number (minimum 10 digits)";
      }
    }

    // Features validation
    if (formData.features.length === 0) {
      newErrors.features =
        "Please select at least one feature you're interested in";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFeatureToggle = (feature) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSubmitDemo = () => {
    if (!validateForm()) return;
    console.log("Demo request submitted:", formData);
    props.requestDemo(formData);
  };

  const getInputClasses = (fieldName) => {
    const baseClasses =
      "w-full bg-white border text-sm rounded-md min-h-[40px] px-3 py-2 transition-all duration-200 shadow-sm focus:outline-none text-slate-800";

    if (errors[fieldName]) {
      return `${baseClasses} border-red-500 border-2 hover:border-red-600 focus:border-red-600 focus:shadow-red-100 focus:shadow-md`;
    }

    return `${baseClasses} border-slate-800 border-opacity-25 hover:shadow-md hover:border-blue-500 focus:shadow-md focus:border-blue-500`;
  };

  const errorClasses = "text-red-600 text-xs text-left";
  return (
    <div className="bg-white min-h-screen">
      <div className="p-2 pb-8 max-w-6xl mx-auto">
        <div className="space-y-2">
          {/* Two-column grid for all screen sizes */}
          <div className="grid grid-cols-2 gap-3">
            {/* First Name */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Daniel"
                value={formData.firstName}
                onChange={(e) => handleFormChange("firstName", e.target.value)}
                className={getInputClasses("firstName")}
              />
              {errors.firstName && (
                <p className={errorClasses}>{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Miller"
                value={formData.lastName}
                onChange={(e) => handleFormChange("lastName", e.target.value)}
                className={getInputClasses("lastName")}
              />
              {errors.lastName && (
                <p className={errorClasses}>{errors.lastName}</p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your company name"
                value={formData.companyName}
                onChange={(e) =>
                  handleFormChange("companyName", e.target.value)
                }
                className={getInputClasses("companyName")}
              />
              {errors.companyName && (
                <p className={errorClasses}>{errors.companyName}</p>
              )}
            </div>

            {/* Event Name */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Event Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your event name"
                value={formData.eventName}
                onChange={(e) => handleFormChange("eventName", e.target.value)}
                className={getInputClasses("eventName")}
              />
              {errors.eventName && (
                <p className={errorClasses}>{errors.eventName}</p>
              )}
            </div>

            {/* Business Email */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Business Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400">@</span>
                <input
                  type="email"
                  placeholder="duncan@memberstack.com"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  className={`${getInputClasses("email")} pl-8`}
                />
              </div>
              {errors.email && <p className={errorClasses}>{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Phone/Whatsapp Number <span className="text-red-500">*</span>
              </label>
              <div className="flex w-full">
                <select
                  value={formData.countryCode}
                  onChange={(e) =>
                    handleFormChange("countryCode", e.target.value)
                  }
                  className={`w-20 flex-shrink-0 bg-white border text-sm rounded-l-md min-h-[40px] px-1 py-2 transition-all duration-200 shadow-sm focus:outline-none text-slate-800 ${
                    errors.phone
                      ? "border-red-500 border-2 hover:border-red-600 focus:border-red-600 focus:shadow-red-100 focus:shadow-md"
                      : "border-slate-800 border-opacity-25 hover:shadow-md hover:border-blue-500 focus:shadow-md focus:border-blue-500"
                  }`}
                >
                  {countryCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="7575853977"
                  value={formData.phone}
                  onChange={(e) => handleFormChange("phone", e.target.value)}
                  className={`flex-1 text-sm min-w-0 bg-white border border-l-0 rounded-r-md min-h-[40px] px-3 py-2 transition-all duration-200 shadow-sm focus:outline-none text-slate-800 ${
                    errors.phone
                      ? "border-red-500 border-2 hover:border-red-600 focus:border-red-600 focus:shadow-red-100 focus:shadow-md"
                      : "border-slate-800 border-opacity-25 hover:shadow-md hover:border-blue-500 focus:shadow-md focus:border-blue-500"
                  }`}
                />
              </div>
              {errors.phone && (
                <p className={`${errorClasses} mt-1`}>{errors.phone}</p>
              )}
            </div>

            {/* Attendees */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Expected Number of Attendees{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.attendees}
                onChange={(e) => handleFormChange("attendees", e.target.value)}
                className={getInputClasses("attendees")}
              >
                {attendeeOptions.map((option) => (
                  <option
                    key={option}
                    value={option === "Select one..." ? "" : option}
                  >
                    {option}
                  </option>
                ))}
              </select>
              {errors.attendees && (
                <p className={errorClasses}>{errors.attendees}</p>
              )}
            </div>

            {/* Industry */}
            <div>
              <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
                Choose Your Event Industry{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.industry}
                onChange={(e) => handleFormChange("industry", e.target.value)}
                className={getInputClasses("industry")}
              >
                {industries.map((industry) => (
                  <option
                    key={industry}
                    value={industry === "Select one..." ? "" : industry}
                  >
                    {industry}
                  </option>
                ))}
              </select>
              {errors.industry && (
                <p className={errorClasses}>{errors.industry}</p>
              )}
            </div>
          </div>

          {/* Features - Full width */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-800 mb-3 text-left">
              Features Interested in{" "}
              <span className="text-gray-500 italic">
                (Choose All That Apply):
              </span>
              <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {features.map((feature) => (
                <button
                  key={feature}
                  type="button"
                  onClick={() => handleFeatureToggle(feature)}
                  className={`px-3 py-2 rounded-full border text-xs transition-all duration-200 ${
                    formData.features.includes(feature)
                      ? "bg-blue-100 border-blue-500 text-blue-700"
                      : "bg-white border-slate-800 border-opacity-25 text-slate-800 hover:border-gray-400 shadow-sm hover:shadow-md"
                  }`}
                >
                  {feature}
                </button>
              ))}
            </div>
            <div className="mt-4">
              {errors.features && (
                <p className={errorClasses}>{errors.features}</p>
              )}
            </div>
          </div>

          {/* Message - Full width */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-800 mb-2 text-left">
              What would you like us to know about your event?{" "}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              placeholder="Type your message..."
              value={formData.message}
              onChange={(e) => handleFormChange("message", e.target.value)}
              rows={4}
              className={`w-full bg-white border text-sm rounded-md min-h-[100px] mb-2.5 px-3 py-2 transition-all duration-200 shadow-sm focus:outline-none text-slate-800 resize-none ${
                errors.message
                  ? "border-red-500 border-2 hover:border-red-600 focus:border-red-600 focus:shadow-red-100 focus:shadow-md"
                  : "border-slate-800 border-opacity-25 hover:shadow-md hover:border-blue-500 focus:shadow-md focus:border-blue-500"
              }`}
            />
            {errors.message && <p className={errorClasses}>{errors.message}</p>}
          </div>

          {/* Privacy Checkbox - Full width */}
          <div className="col-span-2 flex items-start space-x-3">
            <input
              type="checkbox"
              id="privacy"
              checked={formData.agreeToPolicy}
              onChange={(e) =>
                handleFormChange("agreeToPolicy", e.target.checked)
              }
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="privacy"
              className="text-xs text-gray-600 leading-relaxed text-left"
            >
              By submitting, you agree we may contact you and send updates per
              our{" "}
              <a
                href="https://eventify.io/privacy-policy"
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>
              .
            </label>
          </div>
          {errors.agreeToPolicy && (
            <p className={`${errorClasses} col-span-2`}>
              {errors.agreeToPolicy}
            </p>
          )}

          {/* Submit Button - Full width */}
          <div className="col-span-2 pt-4">
            <button
              onClick={handleSubmitDemo}
              disabled={!formData.agreeToPolicy}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Schedule My Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDemo;
